/** The contract's ceiling for an amount: the audit record keeps it in a 32-bit column. */
const MAX_MINOR_UNITS = 2_147_483_647

/** Minor units are what travels; a person reads major ones. */
export function money(value: { readonly amount: number; readonly currency: string }): string {
  try {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: value.currency,
    }).format(value.amount / 100)
  } catch {
    // An unknown ISO code is a configuration somebody should see rather than a
    // crashed screen.
    return `${(value.amount / 100).toLocaleString("pl-PL")} ${value.currency}`
  }
}

/**
 * What a person typed in major units — `350`, `350,50`, `1 234,56` — as the
 * minor units the contract carries, or null when it is not an amount.
 *
 * Read as a string and never through a float: `0.29 * 100` is
 * 28.999999999999996, and the number this feeds decides whether a paid source
 * is asked. Spaces are thousands separators, as the order screen prints them.
 */
export function toMinorUnits(text: string): number | null {
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(text.replace(/\s/g, ""))
  if (match === null) return null
  const [, whole = "", fraction = ""] = match
  const amount = Number(whole) * 100 + Number(fraction.padEnd(2, "0"))
  return Number.isSafeInteger(amount) && amount <= MAX_MINOR_UNITS ? amount : null
}
